import { createApp } from "vue";

import { createI18n, useI18n } from "vue-i18n";
import { languages } from "./i18n/index.js";
import { defaultLocale } from "./i18n/index.js";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { setup as setupSentry } from "@/helpers/sentry.js";
import quizList from "@/common/quiz";

import PerfectScrollbar from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css";

import App from "./App.vue";

import VueTelInput from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import "vue-select/dist/vue-select.css";

import Toast, { POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";

import cookie from "js-cookie";

import { DateTime, Settings } from "luxon";

import router from "./router";
import { useUTM } from "./stores/utmStore.js";
import { useAdmitAd } from "./stores/admitAdStore.js";

import { useIframeEmbed } from "./stores/iframeEmbedStore.js";
import { gtmClickEventDirective } from "./helpers/gtm.js";
import { createSegment } from "@/helpers/segment/createSegment";
import { startSiteEvents } from "@/helpers/siteEvents";
// ----------
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const messages = Object.assign(languages);

const slavicPlural = (choice, choicesLength) => {
  if (choicesLength !== 3) return choice === 1 ? 0 : 1;
  const n = Math.abs(choice);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 1;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 2;
  return 0;
};

const i18n = createI18n({
  legacy: false,
  locale: defaultLocale,
  fallbackLocale: "en",
  globalInjection: true,
  messages,
  pluralRules: { ru: slavicPlural, pl: slavicPlural },
});

createSegment(document.location.href, router);

// Save gclid search param
const gclid = new URL(location.href).searchParams.get("gclid");
if (gclid) {
  cookie.set("gclid", gclid, { expires: 30 });
}

const oppref = new URL(location.href).searchParams.get("oppref");
if (oppref) {
  cookie.set("oppref", oppref, { expires: 30 });
}

const refCodeParam = new URL(location.href).searchParams.get("ref_code");
if (refCodeParam) {
  cookie.set("ref_code", refCodeParam, { expires: 30 });
}

const acquisitionSourceParam = new URL(location.href).searchParams.get("acquisition_source");
if (refCodeParam) {
  cookie.set("acquisition_source", acquisitionSourceParam, { expires: 30 });
}

const date_start = new URL(location.href).searchParams.get("date_start");
const date_end = new URL(location.href).searchParams.get("date_end");

const isQueryParamsRecived = !!date_start && !!date_end;
const isQueryParamsValid =
  isQueryParamsRecived &&
  !isNaN(date_start) &&
  !isNaN(date_end) &&
  Number(date_end) >= Number(date_start);

if (isQueryParamsValid) {
  cookie.set("date_start", date_start);
  cookie.set("date_end", date_end);
} else {
  // Set defaults
  cookie.set("date_start", 0);
  cookie.set("date_end", 30);
}

const currentUrl = new URL(location.href);
const href = currentUrl.href;
const isInIframe = currentUrl.searchParams.get("ifHideHeader") === "true";
const refererParam = isInIframe && currentUrl.searchParams.get("referer");
cookie.set("referer", refererParam || href);

const params = new URLSearchParams(
  href.substring(href.indexOf("?"), href.length)
);
let paramObj = {};
for (let value of params.keys()) {
  if (value !== "segment") {
    paramObj[value] = params.get(value);
  }
}

if (!cookie.get("utmList")) {
  cookie.set("utmList", JSON.stringify(paramObj));
  cookie.set("utmString", href.substring(href.indexOf("?")));
}

const app = createApp(App, {
  setup() {
    const { t } = useI18n();
    return { t };
  },
})
  .use(router)
  .use(VueTelInput)
  .use(Toast, {
    position: POSITION.BOTTOM_RIGHT,
    timeout: 2000,
    closeOnClick: false,
    pauseOnFocusLoss: false,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: true,
    closeButton: "button",
    icon: true,
    rtl: false,
  })
  .use(i18n)
  .use(pinia)
  .use(PerfectScrollbar)
  .directive("gtm-click-event", gtmClickEventDirective);
// .use(VueSmoothScroll)

// UTM store
const utmStore = useUTM();
utmStore.init();

// AdmitAd store
const admitAdStore = useAdmitAd();
admitAdStore.init(utmStore);

// iframeEmbed store
const iframeEmbed = useIframeEmbed();
iframeEmbed.init();

app.config.globalProperties.$luxonDateTime = DateTime;
app.config.globalProperties.$luxonSettings = Settings;

app.mount("#app");

// Sentry
setupSentry(app, router);

startSiteEvents(router, () => i18n.global.locale.value);

// Yandex.Metrika
(function (m, e, t, r, i, k, a) {
  m[i] =
    m[i] ||
    function () {
      (m[i].a = m[i].a || []).push(arguments);
    };
  m[i].l = 1 * new Date();
  for (var j = 0; j < document.scripts.length; j++) {
    if (document.scripts[j].src === r) {
      return;
    }
  }
  (k = e.createElement(t)),
    (a = e.getElementsByTagName(t)[0]),
    (k.async = 1),
    (k.src = r),
    a.parentNode.insertBefore(k, a);
})(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

ym(90082538, "init", {
  defer: true,
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
  webvisor: true,
});
// /Yandex.Metrika
